/**
 * Attack Surface Cell Renderer
 *
 * Renders the vulnerability's attack surface classification as chips.
 * The values are unioned onto the risk node at write time via
 * InheritAttackSurface (tabularium) and share the shape of
 * Asset.attackSurface (external/internal/cloud/repository/application/llm).
 */

import React from 'react';

import type { RiskWithVulnerability } from '@/hooks/useRisks';
import { AttackSurfaceComponent } from '@/sections/asset/components/tabs';
import { useCellFilters } from '@/sections/vulnerabilities/components/cells/CellFiltersContext';

interface AttackSurfaceCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for the vulnerability attack surface column
 *
 * Displays the first attack surface value as a chip with a "+N" overflow
 * (all values listed in the tooltip), prioritizing values that match the
 * table's active attack surface filters.
 *
 * @param risk - The vulnerability/risk with the attackSurface array
 * @returns AttackSurfaceComponent chips or dash if no values
 */
export const AttackSurfaceCell: React.FC<AttackSurfaceCellProps> = React.memo(
  ({ risk }) => {
    // Active attack surface filters from the vulnerabilities table.
    const filters = useCellFilters();
    const activeAttackSurfaceFilters = filters?.advancedFilters?.attackSurface;

    // Dash fallback for consistency with OriginCell.
    if (!risk.attackSurface?.length) return <>-</>;

    return (
      <AttackSurfaceComponent
        attackSurface={risk.attackSurface}
        className="max-w-[150px] min-w-[150px]"
        maxVisible={1}
        size="xs"
        wrapInTooltip
        activeFilters={activeAttackSurfaceFilters}
      />
    );
  }
);

AttackSurfaceCell.displayName = 'AttackSurfaceCell';
